import React from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { ReactComponent as PenIcon } from '../../../assets/icons/Pen.svg';
import { getGenderDisplayLabel, getRaceDisplayLabel } from '../../../utils/constants';
import { formatHeightInCm } from '../../../utils';
import {
  isScanOrPlusMembership,
  normalizeSupplementsFromApi
} from '../../../types/membersTypes';

type MedicalCardAddress = {
  full_address?: string | null;
  postcode?: string | null;
  city?: string | null;
  country?: string | null;
  address_line_1?: string | null;
};

const getMedicalCardAddress = (address: unknown): MedicalCardAddress =>
  (address && typeof address === 'object' ? address : {}) as MedicalCardAddress;

const formatStoredDetailOrNo = (value: unknown): string => {
  if (value === undefined || value === null) return '-';
  const text = String(value).trim();
  if (!text) return 'No';
  return text;
};

const formatListValue = (value: unknown): string => {
  if (value === undefined || value === null) return '-';
  if (Array.isArray(value)) {
    const cleaned = value.map((item) => String(item).trim()).filter(Boolean);
    return cleaned.length ? cleaned.join(', ') : '-';
  }
  const text = String(value).trim();
  return text || '-';
};

type Props = {
  medicalCard: any;
  memberData: any;
  visible: boolean;
  onEditSection: (title: string) => void;
};

const MedicalCardOnboardingSections: React.FC<Props> = ({
  medicalCard,
  memberData,
  visible,
  onEditSection
}) => {
  const address = getMedicalCardAddress(medicalCard?.basics?.address);
  const scanInfo = (medicalCard?.scan_info || {}) as Record<string, any>;
  const showScanSections = isScanOrPlusMembership(memberData?.membership_type);
  const cancerHistoryRaw = scanInfo.cancer_history;
  const cancerHistoryDisplay =
    cancerHistoryRaw === 'Yes' || cancerHistoryRaw === 'yes'
      ? scanInfo.cancer_history_details
        ? `Yes — ${scanInfo.cancer_history_details}`
        : 'Yes'
      : cancerHistoryRaw === 'No' || cancerHistoryRaw === 'no'
        ? 'No'
        : cancerHistoryRaw || '-';

  const EditButton = ({ title }: { title: string }) =>
    visible ? (
      <button
        type='button'
        className='border-btn'
        onClick={() => onEditSection(title)}
      >
        <PenIcon />
        Edit
      </button>
    ) : null;

  return (
    <>
      {/* 1. Personal Information */}
      <div>
        <div className='d-flex justify-content-between align-items-center section-block-title'>
          <h3 className=''>Personal Information</h3>
          <EditButton title='Personal Information' />
        </div>
        <div className='key-value-row'>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Full name:</span>
              <span className='key-value'>
                {`${medicalCard?.basics?.first_name || memberData?.first_name || ''} ${
                  medicalCard?.basics?.last_name || memberData?.last_name || ''
                }`.trim() || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Date of Birth:</span>
              <span className='key-value'>
                {medicalCard?.basics?.birth_date
                  ? formatInTimeZone(
                      new Date(medicalCard.basics.birth_date),
                      'UTC',
                      'dd/MM/yyyy'
                    )
                  : memberData?.dob
                    ? formatInTimeZone(new Date(memberData.dob), 'UTC', 'dd/MM/yyyy')
                    : '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Sex Assigned at Birth:</span>
              <span className='key-value'>
                {getGenderDisplayLabel(
                  medicalCard?.basics?.biological_sex || memberData?.gender
                )}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Email:</span>
              <span className='key-value'>
                {medicalCard?.basics?.email || memberData?.email || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Mobile phone number:</span>
              <span className='key-value'>
                {medicalCard?.basics?.phone || memberData?.phone || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Weight:</span>
              <span className='key-value'>
                {medicalCard?.basics?.weight
                  ? `${medicalCard.basics.weight} ${medicalCard.basics.weight_unit}`
                  : '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Height:</span>
              <span className='key-value'>
                {formatHeightInCm(medicalCard?.basics?.height)}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Race/ethnicity:</span>
              <span className='key-value'>
                {getRaceDisplayLabel(medicalCard?.basics?.race_ethnicity)}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Address:</span>
              <span className='key-value'>
                {address.full_address || address.address_line_1 || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>City:</span>
              <span className='key-value'>{address.city ?? '-'}</span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Postal code:</span>
              <span className='key-value'>{address.postcode ?? '-'}</span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Country:</span>
              <span className='key-value'>{address.country ?? '-'}</span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Created at:</span>
              <span className='key-value'>
                {medicalCard?.created_at
                  ? formatInTimeZone(medicalCard.created_at, 'UTC', 'dd/MM/yyyy')
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Current Lifestyle */}
      <div>
        <div className='d-flex justify-content-between align-items-center section-block-title'>
          <h3 className=''>Current Lifestyle</h3>
          <EditButton title='Current Lifestyle' />
        </div>
        <div className='key-value-row'>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Overall health:</span>
              <span className='key-value'>
                {medicalCard?.health_lifestyle?.overall_health || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Activity level:</span>
              <span className='key-value'>
                {medicalCard?.health_lifestyle?.activity_level || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Exercise routine:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.exercise_routine || '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_exercise_routine && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other exercise routine:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_exercise_routine}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Smoking status:</span>
              <span className='key-value'>
                {medicalCard?.health_lifestyle?.smoking_status || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Alcohol frequency:</span>
              <span className='key-value'>
                {medicalCard?.health_lifestyle?.alcohol_frequency || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Working style:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.working_style || '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_working_style && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other working style:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_working_style}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Diet types:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.diet_types?.length
                  ? medicalCard.nutritionist_questions.diet_types.join(', ')
                  : '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_diet_type && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other diet type:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_diet_type}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Eating pattern:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.eating_pattern || '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_eating_pattern && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other eating pattern:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_eating_pattern}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Hydration:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.hydration || '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_hydration && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other hydration:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_hydration}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Calming activities:</span>
              <span className='key-value'>
                {medicalCard?.nutritionist_questions?.calming_activities?.length
                  ? medicalCard.nutritionist_questions.calming_activities.join(', ')
                  : '-'}
              </span>
            </div>
          </div>
          {medicalCard?.nutritionist_questions?.other_calming_activity && (
            <div className='key-value-wrapper'>
              <div className='key d-flex'>
                <span className='key-label'>Other calming activity:</span>
                <span className='key-value'>
                  {medicalCard.nutritionist_questions.other_calming_activity}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3. Medical History */}
      <div>
        <div className='d-flex justify-content-between align-items-center section-block-title'>
          <h3 className=''>Medical History</h3>
          <EditButton title='Medical History' />
        </div>
        <div className='key-value-row'>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Medical conditions:</span>
              <span className='key-value'>
                {formatStoredDetailOrNo(medicalCard?.health_lifestyle?.medical_conditions)}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Previous surgeries:</span>
              <span className='key-value'>
                {formatStoredDetailOrNo(
                  (medicalCard?.health_lifestyle as any)?.previous_surgeries
                )}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Medications / supplements:</span>
              <span className='key-value'>
                {formatStoredDetailOrNo(
                  medicalCard?.health_lifestyle?.supplements != null
                    ? normalizeSupplementsFromApi(medicalCard.health_lifestyle.supplements)
                    : medicalCard?.health_lifestyle?.medications
                )}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Allergies:</span>
              <span className='key-value'>
                {formatStoredDetailOrNo(medicalCard?.health_lifestyle?.allergies)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Female Sex Hormones */}
      {medicalCard?.basics?.biological_sex === 'female' && (
        <div>
          <div className='d-flex justify-content-between align-items-center section-block-title'>
            <h3 className=''>Female Sex Hormones</h3>
            <EditButton title='Female Sex Hormones' />
          </div>
          <div className='key-value-row'>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Last menstrual period:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.last_period_date || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Hormonal contraception or HRT:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.hormonal_contraception_hrt || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Cycle length:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.typical_cycle_length || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Primary reason for testing:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.primary_testing_reason || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Menstrual flow characteristics:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.menstrual_flow_characteristics || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>
                  Age at last menstrual period or whether surgical menopause occurred:
                </span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.menopause_details || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Additional notes for clinical team:</span>
                <span className='key-value'>
                  {medicalCard?.female_sex_hormones?.additional_notes_for_clinical_team || '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 5. MRI Scan Safety */}
      {showScanSections && (
        <div>
          <div className='d-flex justify-content-between align-items-center section-block-title'>
            <h3 className=''>MRI Scan Safety and Clinical Considerations</h3>
            <EditButton title='MRI Scan Safety and Clinical Considerations' />
          </div>
          <div className='key-value-row'>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Reason for scan:</span>
                <span className='key-value'>{scanInfo.reason_for_scan || '-'}</span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Current symptoms:</span>
                <span className='key-value'>{scanInfo.current_symptoms || '-'}</span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Cancer history:</span>
                <span className='key-value'>{cancerHistoryDisplay}</span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Previous imaging:</span>
                <span className='key-value'>{scanInfo.any_previous_imaging || '-'}</span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Family history (scan):</span>
                <span className='key-value'>
                  {scanInfo.scan_family_history_details || '-'}
                </span>
              </div>
            </div>
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Medical implants:</span>
                <span className='key-value'>
                  {formatListValue(scanInfo.medical_implants)}
                </span>
              </div>
            </div>
            {scanInfo.metallic_implants_details && (
              <div className='key-value-wrapper' style={{ width: '100%' }}>
                <div className='key d-flex'>
                  <span className='key-label'>Implant details:</span>
                  <span className='key-value'>
                    {scanInfo.metallic_implants_details}
                  </span>
                </div>
              </div>
            )}
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Practical considerations:</span>
                <span className='key-value'>
                  {formatListValue(scanInfo.practical_considerations)}
                </span>
              </div>
            </div>
            {scanInfo.practical_considerations_details && (
              <div className='key-value-wrapper' style={{ width: '100%' }}>
                <div className='key d-flex'>
                  <span className='key-label'>Practical consideration details:</span>
                  <span className='key-value'>
                    {scanInfo.practical_considerations_details}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 6. Goals */}
      <div>
        <div className='d-flex justify-content-between align-items-center section-block-title'>
          <h3 className=''>Goals</h3>
          <EditButton title='Goals' />
        </div>
        <div className='key-value-row'>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Goals:</span>
              <span className='key-value'>
                {medicalCard?.goals?.goals?.length
                  ? medicalCard.goals.goals.join(', ')
                  : '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Health concerns:</span>
              <span className='key-value'>
                {medicalCard?.goals?.health_concerns?.length
                  ? medicalCard.goals.health_concerns.join(', ')
                  : '-'}
              </span>
            </div>
          </div>
          {medicalCard?.goals?.health_concern_details && (
            <div className='key-value-wrapper' style={{ width: '100%' }}>
              <div className='key d-flex'>
                <span className='key-label'>Health concern details:</span>
                <span className='key-value'>
                  {(medicalCard.goals as any).health_concern_details}
                </span>
              </div>
            </div>
          )}
          <div className='key-value-wrapper'>
            <div className='key d-flex'>
              <span className='key-label'>Last blood test:</span>
              <span className='key-value'>
                {medicalCard?.goals?.last_blood_test || '-'}
              </span>
            </div>
          </div>
          <div className='key-value-wrapper' style={{ width: '100%' }}>
            <div className='key d-flex'>
              <span className='key-label'>Additional info:</span>
              <span className='key-value'>
                {medicalCard?.goals?.additional_info || '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MedicalCardOnboardingSections;
